import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  Chip,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { delftBlue } from "@/theme/colors";
import FormInput from "@/components/forms/FormInput";
import FormTextArea from "@/components/forms/FormTextArea";
import {
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
} from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Company name is required")
    .min(2, "Company name must be at least 2 characters")
    .max(100, "Company name must be at most 100 characters")
    .trim(),
  description: Yup.string()
    .max(500, "Description must be at most 500 characters")
    .trim(),
  website: Yup.string()
    .url("Must be a valid URL")
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  phoneNumber: Yup.string()
    .max(10, "Phone number must be at most 10 characters")
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  address: Yup.string()
    .max(200, "Address must be at most 200 characters")
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
});

const CompanyFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isEditMode = !!id;

  const {
    data: companyData,
    isLoading: isLoadingCompany,
    error: companyError,
  } = useGetCompanyQuery(id, {
    skip: !isEditMode,
  });

  const [createCompany, { isLoading: isCreating }] = useCreateCompanyMutation();
  const [updateCompany, { isLoading: isUpdating }] = useUpdateCompanyMutation();

  const company = companyData?.data?.company || companyData?.company;

  const initialValues = {
    name: company?.name || "",
    description: company?.description || "",
    website: company?.website || "",
    phoneNumber: company?.phoneNumber || "",
    address: company?.address || "",
  };

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || "",
        website: values.website?.trim() || "",
        phoneNumber: values.phoneNumber?.trim() || "",
        address: values.address?.trim() || "",
      };

      if (isEditMode) {
        const { error } = await updateCompany({ id, ...payload });
        if (error) {
          setStatus({
            success: false,
            message: error?.data?.message || "Failed to update company",
          });
          setSubmitting(false);
          return;
        }
      } else {
        const { error } = await createCompany(payload);
        if (error) {
          setStatus({
            success: false,
            message: error?.data?.message || "Failed to create company",
          });
          setSubmitting(false);
          return;
        }
      }

      navigate("/companies", { state: { refresh: true } });
    } catch (error) {
      setStatus({
        success: false,
        message: error.message || "An error occurred",
      });
      setSubmitting(false);
    }
  };

  // Check if user can add/edit companies (only Super Admin)
  const canManageCompanies = user?.role === Roles.SUPER_ADMIN;

  if (!canManageCompanies) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">
          You don't have permission to manage companies. Only Super Admin can
          manage companies.
        </Alert>
        <Button onClick={() => navigate("/companies")} sx={{ mt: 2 }}>
          Back to Companies
        </Button>
      </Box>
    );
  }

  if (isEditMode && isLoadingCompany) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isEditMode && companyError) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">
          {companyError?.data?.message || "Failed to load company"}
        </Alert>
        <Button onClick={() => navigate("/companies")} sx={{ mt: 2 }}>
          Back to Companies
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleSubmit, isSubmitting, status }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Header */}
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 22, sm: 24, md: 28 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.29,
                    marginBottom: 1,
                  }}
                >
                  {isEditMode ? "Edit Company" : "Add New Company"}
                </Typography>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 13, sm: 14, md: 16 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  {isEditMode
                    ? "Update company details"
                    : "Create a new company"}
                </Typography>
              </Box>

              {/* Error Message */}
              {status?.message && (
                <Alert severity="error">{status.message}</Alert>
              )}

              {/* Form Fields */}
              <Stack spacing={2}>
                <FormInput
                  name="name"
                  label="Company Name"
                  placeholder="e.g., Acme Corporation"
                  required
                />
                <FormTextArea
                  name="description"
                  label="Description"
                  placeholder="Enter company description"
                />
                <FormInput
                  name="website"
                  label="Website"
                  placeholder="https://example.com"
                  type="url"
                />
                <FormInput
                  name="phoneNumber"
                  label="Phone Number"
                  placeholder="04XXXXXXXX"
                />
                <FormTextArea
                  name="address"
                  label="Address"
                  placeholder="Enter company address"
                />
              </Stack>

              {isEditMode && (
                <Box
                  sx={{
                    padding: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    outline: `1px ${delftBlue[100]} solid`,
                    background: "white",
                  }}
                >
                  <Typography
                    sx={{
                      color: delftBlue[500],
                      fontFamily: "Audiowide, sans-serif",
                      fontSize: 18,
                      marginBottom: 0.5,
                    }}
                  >
                    Assigned activities
                  </Typography>
                  <Typography
                    sx={{
                      color: delftBlue[400],
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: 13,
                      marginBottom: 2,
                    }}
                  >
                    Form versions this company currently uses
                  </Typography>
                  {(company?.assignedActivities || []).length ? (
                    <Stack spacing={1.25}>
                      {company.assignedActivities.map((row) => (
                        <Stack
                          key={row.activityId}
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ sm: "center" }}
                          sx={{
                            padding: 1.5,
                            borderRadius: 1.5,
                            background: delftBlue[50],
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 600,
                                fontSize: 14,
                                color: delftBlue[600],
                              }}
                            >
                              {row.activityName}
                            </Typography>
                            {row.activityCode ? (
                              <Typography
                                sx={{
                                  fontFamily: "Montserrat, sans-serif",
                                  fontSize: 12,
                                  color: delftBlue[400],
                                }}
                              >
                                {row.activityCode}
                              </Typography>
                            ) : null}
                          </Box>
                          {row.publishedVersion != null ? (
                            <Chip
                              size="small"
                              label={
                                row.versionName
                                  ? `v${row.publishedVersion} — ${row.versionName}`
                                  : `v${row.publishedVersion}`
                              }
                              sx={{ fontFamily: "Montserrat, sans-serif" }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="No form version"
                              sx={{ fontFamily: "Montserrat, sans-serif" }}
                            />
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      sx={{
                        color: delftBlue[400],
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: 13,
                      }}
                    >
                      No activities assigned to this company
                    </Typography>
                  )}
                </Box>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate("/companies")}
                  disabled={isSubmitting || isCreating || isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || isCreating || isUpdating}
                  sx={{
                    backgroundColor: delftBlue[500],
                    "&:hover": {
                      backgroundColor: delftBlue[600],
                    },
                  }}
                >
                  {isSubmitting || isCreating || isUpdating ? (
                    <CircularProgress size={24} />
                  ) : isEditMode ? (
                    "Update Company"
                  ) : (
                    "Create Company"
                  )}
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CompanyFormPage;
